import React, { useEffect, useRef, useState } from "react";
import type { CheckoutZone, DropZoneItem } from "@/networking/drop-zones.api";
import useIdentifyDropzone from "@/hooks/useIdentifyDropzone";

// `allow-popups-to-escape-sandbox` and `allow-top-navigation-by-user-activation`
// are only applied to Fluid-controlled hosts to keep the trust boundary tight;
// non-Fluid embeds keep the original stricter sandbox (no behavior change for
// any existing dropzone). Top navigation requires a user gesture, so a Fluid
// dropzone can navigate the parent page on click (e.g. the rep-portal CTA on
// order confirmation) but cannot redirect it on its own.
// `run.app` is a shared Google Cloud Run domain, so we additionally require
// the leftmost label to start with `droplet-` (Fluid's droplet naming
// convention) to avoid matching unrelated Cloud Run services.
const FLUID_EMBED_HOST_PATTERNS = [
  /^([a-z0-9-]+\.)*fluid\.app$/i,
  /^droplet-[a-z0-9-]+(\.[a-z0-9-]+)*\.run\.app$/i,
  /^localhost(:\d+)?$/i,
];

function isFluidControlledHost(rawUrl: string): boolean {
  try {
    return FLUID_EMBED_HOST_PATTERNS.some((re) =>
      re.test(new URL(rawUrl).hostname),
    );
  } catch {
    return false;
  }
}

function RenderDropZone({
  dropZone,
  token,
  companyId,
}: {
  dropZone: DropZoneItem | null;
  token: string;
  companyId?: number;
}) {
  const [resizeOverride, setResizeOverride] = useState<number | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeHeight = resizeOverride ?? dropZone?.settings.height ?? 120;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      let embedOrigin: string | null = null;
      try {
        embedOrigin = dropZone?.embed_url
          ? new URL(dropZone.embed_url).origin
          : null;
      } catch {
        return;
      }
      if (!event.origin || event.origin !== embedOrigin) {
        return;
      }
      // Distinct iframes from the same origin share `event.origin`, so the
      // origin filter alone allows one dropzone's RESIZE to apply to a
      // sibling dropzone. `event.source` is the emitting iframe's Window —
      // each one is unique — so we additionally require it to match THIS
      // iframe.
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      if (
        event.data &&
        typeof event.data === "object" &&
        event.data.type === "RESIZE" &&
        typeof event.data.height === "number" &&
        event.data.height > 0 &&
        event.data.height < 10000
      ) {
        setResizeOverride(event.data.height + 16);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [dropZone?.embed_url]);

  if (!dropZone) {
    return null;
  }

  try {
    const parsed = new URL(dropZone.embed_url);
    if (parsed.protocol !== "https:") {
      return null;
    }
  } catch {
    return null;
  }

  const params = new URLSearchParams({ token });
  if (companyId) {
    params.set("company_id", companyId.toString());
  }
  const embedUrl = `${dropZone.embed_url}?${params.toString()}`;
  const sandboxAttr = isFluidControlledHost(dropZone.embed_url)
    ? "allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
    : "allow-same-origin allow-scripts";

  return (
    <iframe
      ref={iframeRef}
      key={dropZone.uuid}
      src={embedUrl}
      className="my-2 w-full rounded"
      style={{
        height: `${iframeHeight}px`,
        boxSizing: "border-box",
      }}
      sandbox={sandboxAttr}
      referrerPolicy="no-referrer"
    />
  );
}

export default function DropZone({
  zone,
  token,
  subdomain,
  page,
  companyId,
}: {
  zone: CheckoutZone;
  token: string;
  subdomain: string | null;
  page: "checkout" | "order_confirmation";
  companyId?: number;
}) {
  const { getZone } = useIdentifyDropzone(token, subdomain ?? "", page);
  return (
    <RenderDropZone
      dropZone={getZone(zone)}
      token={token}
      companyId={companyId}
    />
  );
}
